/* ============================================================================
   SHARK TANK — CONSTANTS & MOCK DATA (FRONTEND ONLY)
   All option lists, field configurations, verification states, navigation
   configs, and mock data for the Shark Tank page live here.
   ============================================================================ */

let uidCounter = 1;
export const uid = () => `id_${uidCounter++}`;

export const initials = (name) =>
  name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

/* ---------------------------------------------------------------------------
   OPTION LISTS
--------------------------------------------------------------------------- */
export const INDUSTRIES = ["Technology","SaaS","FinTech","HealthTech","AgriTech","E-commerce","Manufacturing","Hospitality","Travel & Tourism","Real Estate","Education","Renewable Energy","Retail","Logistics","Media & Entertainment","Food & Beverage","Other"];
export const CATEGORIES = ["Early Stage Startup","Growth Stage Company","Established Business","Social Enterprise","Franchise","Other"];
export const COUNTRIES = ["India","United States","United Kingdom","United Arab Emirates","Singapore","Australia","Canada","Other"];
export const CURRENCIES = ["INR","USD","GBP","AED","SGD","EUR","Other"];
export const STAGES = ["Early Stage","Growth Stage","Established Business","Any","Other"];
export const INVESTMENT_TYPES = ["Equity","Convertible","Debt","Strategic Investment","Other"];
export const FREQUENCIES = ["One-time","Multiple Investments","Recurring / Periodic","Other"];

export const PHONE_CODES = [
  { code:"+91", iso:"IN", label:"IN +91" },
  { code:"+1", iso:"US", label:"US +1" },
  { code:"+44", iso:"GB", label:"GB +44" },
  { code:"+971", iso:"AE", label:"AE +971" },
  { code:"+65", iso:"SG", label:"SG +65" },
  { code:"+61", iso:"AU", label:"AU +61" },
  { code:"+1", iso:"CA", label:"CA +1" },
  { code:"+86", iso:"CN", label:"CN +86" },
  { code:"+81", iso:"JP", label:"JP +81" },
  { code:"+49", iso:"DE", label:"DE +49" },
  { code:"+33", iso:"FR", label:"FR +33" },
  { code:"+39", iso:"IT", label:"IT +39" },
  { code:"+34", iso:"ES", label:"ES +34" },
  { code:"+55", iso:"BR", label:"BR +55" },
  { code:"+27", iso:"ZA", label:"ZA +27" },
  { code:"+92", iso:"PK", label:"PK +92" },
  { code:"+880", iso:"BD", label:"BD +880" },
  { code:"+94", iso:"LK", label:"LK +94" },
  { code:"+977", iso:"NP", label:"NP +977" },
];
export const STRATEGIC_SUPPORT = ["Strategic Introductions","Partnerships","Hiring Support","Sales","Marketing","Fundraising Support","International Expansion","Business Development","Industry Network","Other"];
export const CONTRIBUTION_AREAS = ["Strategy","Business Development","Sales","Marketing","Technology","Product","Finance","Operations","Human Resources","Legal","Hospitality","Travel","International Expansion","Partnerships","Procurement","Other"];
export const TIME_COMMITMENTS = ["2–5 hours/week","5–10 hours/week","10–20 hours/week","20+ hours/week","Project-based","Advisory only","Other"];
export const ENGAGEMENT_TYPES = ["Advisor","Operating Partner","Strategic Partner","Consultant","Business Development Partner","Technology Partner","Industry Expert","Other"];
export const MENTOR_AREAS = ["Business Strategy","Fundraising","Sales","Marketing","Finance","Operations","Leadership","Technology","Product","Hospitality","Travel","International Expansion","Partnerships","Other"];
export const BUSINESS_TYPES = ["B2B","B2C","B2B + B2C","Other"];
export const FUNDING_TYPES = ["Equity","Debt","Convertible","Strategic Investment","Other"];
export const EXPANSION_PLANS = ["New Offices","New Markets","New Products","International Expansion","New Customer Segments","Other"];
export const USE_OF_FUNDS_CATEGORIES = ["Expansion","Marketing","Technology","Hiring","New Offices","Inventory","Working Capital","Debt Repayment","Other"];

export const CASH_INVESTOR_DECLARATIONS = [
  "I confirm that I have the financial capacity and willingness to invest actual capital into businesses listed on the IHN platform.",
  "I understand that all investments carry inherent risk, including the potential loss of part or all of the invested capital.",
  "I acknowledge that the information I have provided is accurate, complete, and truthful to the best of my knowledge.",
  "I agree that the IHN Team may verify my financial credentials and investment history as part of the due diligence process.",
  "I understand that investment decisions are solely at my discretion and IHN does not guarantee any returns or outcomes.",
  "I confirm that I will comply with all applicable laws, regulations, and tax obligations related to my investments.",
  "I acknowledge that I will maintain confidentiality regarding all business details and proprietary information shared by listed businesses.",
  "I agree to engage in good faith with businesses on the platform and uphold ethical investment practices.",
  "I understand that the IHN platform acts as a facilitator and is not a party to any investment agreement between me and a business.",
  "I consent to being contacted by the IHN Team and listed businesses regarding investment opportunities and updates.",
];

export const SWEAT_INVESTOR_DECLARATIONS = [
  "I confirm that I am willing to contribute my expertise, time, skills, or network to support businesses on the IHN platform.",
  "I acknowledge that my contribution is based on professional expertise and does not guarantee any financial return unless separately agreed.",
  "I confirm that the skills and experience I have listed are accurate and representative of my actual capabilities.",
  "I agree that the IHN Team may verify my professional background and references as part of the onboarding process.",
  "I understand that sweat equity arrangements are subject to mutual agreement with the business and are not enforced by IHN.",
  "I commit to fulfilling my agreed-upon commitments within the defined timeframe and scope of the arrangement.",
  "I acknowledge that I will maintain confidentiality regarding all business details and proprietary information shared by listed businesses.",
  "I agree to engage in good faith and uphold professional and ethical standards in all interactions with businesses.",
  "I understand that the IHN platform acts as a facilitator and is not a party to any sweat equity or contribution agreement.",
  "I consent to being contacted by the IHN Team and listed businesses regarding contribution opportunities and updates.",
];

export const MENTOR_DECLARATIONS = [
  "I confirm that I am willing to provide business advice, guidance, and mentorship to businesses on the IHN platform.",
  "I acknowledge that my mentorship is advisory in nature and does not constitute a formal business relationship or investment.",
  "I confirm that the experience and expertise I have listed are accurate and reflective of my professional background.",
  "I agree that the IHN Team may verify my professional background and references as part of the onboarding process.",
  "I understand that mentoring outcomes depend on the mentee's implementation and are not guaranteed by IHN or myself.",
  "I commit to providing honest, constructive, and responsible guidance to the best of my ability and experience.",
  "I acknowledge that I will maintain confidentiality regarding all business details and proprietary information shared by mentees.",
  "I agree to engage in good faith and uphold professional and ethical standards in all mentorship interactions.",
  "I understand that the IHN platform acts as a facilitator and is not a party to any mentorship arrangement.",
  "I consent to being contacted by the IHN Team and businesses regarding mentorship opportunities and updates.",
];

export const VERIFICATION_STATES = ["Draft","Submitted","Under Review","Additional Information Required","Verified","Rejected","Access Granted"];

export const badgeClassForState = (s) => ({
  "Draft": "SharkBadgeDraft",
  "Submitted": "SharkBadgeSubmitted",
  "Under Review": "SharkBadgeReview",
  "Additional Information Required": "SharkBadgeInfo",
  "Verified": "SharkBadgeVerified",
  "Rejected": "SharkBadgeRejected",
  "Access Granted": "SharkBadgeAccess",
}[s] || "SharkBadgeDraft");

/* ---------------------------------------------------------------------------
   MOCK DATA
--------------------------------------------------------------------------- */
export const MOCK_CASH_INVESTORS = ["Rohan Malhotra","Anjali Mehta","Vikram Sinha"];
export const MOCK_SWEAT_INVESTORS = ["Arjun Bhatt","Neha Kapoor","Sameer Ansari"];
export const MOCK_MENTORS = [
  { name:"Devika Rao", industry:"FinTech", years:18, expertise:"Scaling payments platforms, fundraising strategy" },
  { name:"Karan Oberoi", industry:"Hospitality", years:22, expertise:"Operations, franchise expansion" },
  { name:"Priya Nair", industry:"HealthTech", years:14, expertise:"Regulatory strategy, clinical partnerships" },
];

export const MOCK_OPPORTUNITIES = [
  { id:uid(), name:"TechNova Solutions", category:"Growth Stage Company", industry:"SaaS", location:"Bengaluru, India", funding:"₹2 Cr", equity:"15%", description:"AI-driven workflow automation for mid-market enterprises, expanding into APAC.", posted:"2026-08-01", status:"Verified" },
  { id:uid(), name:"AgriConnect Pvt. Ltd.", category:"Growth Stage Company", industry:"AgriTech", location:"Pune, India", funding:"₹3.5 Cr", equity:"12%", description:"Direct farm-to-retail supply chain platform connecting 4,000+ farmers.", posted:"2026-07-22", status:"Verified" },
  { id:uid(), name:"MediAI Health", category:"Early Stage Startup", industry:"HealthTech", location:"Hyderabad, India", funding:"₹1.2 Cr", equity:"10%", description:"Diagnostic imaging AI reducing radiology turnaround time.", posted:"2026-08-10", status:"Verified" },
  { id:uid(), name:"GreenWatt Energy", category:"Established Business", industry:"Renewable Energy", location:"Ahmedabad, India", funding:"₹6 Cr", equity:"18%", description:"Rooftop solar EPC with recurring maintenance contracts.", posted:"2026-06-30", status:"Verified" },
  { id:uid(), name:"HealthBuddy Solutions", category:"Growth Stage Company", industry:"HealthTech", location:"Delhi, India", funding:"₹2.8 Cr", equity:"14%", description:"Subscription primary-care network across tier-2 cities.", posted:"2026-08-14", status:"Verified" },
  { id:uid(), name:"UrbanNest Realty", category:"Established Business", industry:"Real Estate", location:"Mumbai, India", funding:"₹9 Cr", equity:"9%", description:"Managed co-living operator with 1,800 beds across 6 cities.", posted:"2026-07-05", status:"Verified" },
];

/* ---------------------------------------------------------------------------
   FIELD CONFIGS
--------------------------------------------------------------------------- */
export const PERSONAL_FIELDS = (req = true) => [
  { name:"fullName", label:"Full Name", type:"text", required:true },
  { name:"email", label:"Email", type:"email", required:true },
  { name:"mobile", label:"Mobile Number", type:"tel-country", codeKey:"mobileCode", required:true },
  { name:"country", label:"Country", type:"select-other", options:COUNTRIES, required:true, otherKey:"countryOther" },
  { name:"city", label:"City / Location", type:"text", required:true },
  { name:"designation", label:"Current Designation", type:"text", required:req },
  { name:"company", label:"Company / Organization", type:"text" },
  { name:"website", label:"Website / LinkedIn", type:"text" },
  { name:"experience", label:"Years of Professional Experience", type:"number", required:true },
  { name:"industry", label:"Current Industry", type:"select-other", options:INDUSTRIES, required:true, otherKey:"industryOther" },
  { name:"expertise", label:"Areas of Expertise", type:"textarea", required:true, span2:true, maxWords:200 },
];

export const CASH_INVESTMENT_CAPACITY_FIELDS = [
  { name:"minInvestment", label:"Minimum Investment Amount", type:"number", required:true },
  { name:"maxInvestment", label:"Maximum Investment Amount", type:"number", required:true },
  { name:"currency", label:"Preferred Currency", type:"select-other", options:CURRENCIES, required:true, otherKey:"currencyOther" },
  { name:"frequency", label:"Investment Frequency", type:"select-other", options:FREQUENCIES, otherKey:"frequencyOther" },
];
export const CASH_PREFERENCES_FIELDS = [
  { name:"prefCategories", label:"Preferred Business Categories", type:"select-other", options:CATEGORIES, required:true, otherKey:"prefCategoriesOther" },
  { name:"prefIndustries", label:"Preferred Industries", type:"select-other", options:INDUSTRIES, required:true, otherKey:"prefIndustriesOther" },
  { name:"prefGeography", label:"Preferred Geography", type:"text" },
  { name:"prefStage", label:"Preferred Investment Stage", type:"select-other", options:STAGES, otherKey:"prefStageOther" },
  { name:"prefType", label:"Preferred Investment Type", type:"select-other", options:INVESTMENT_TYPES, otherKey:"prefTypeOther" },
  { name:"prefEquityRange", label:"Preferred Equity Range", type:"text" },
];
export const PAST_INVESTMENT_FIELDS = [
  { name:"company", label:"Company Name", type:"text" },
  { name:"industry", label:"Industry", type:"select", options:INDUSTRIES },
  { name:"date", label:"Investment Date", type:"text" },
  { name:"amount", label:"Amount Invested", type:"number" },
  { name:"equity", label:"Equity / Ownership %", type:"number" },
  { name:"type", label:"Investment Type", type:"select", options:INVESTMENT_TYPES },
  { name:"status", label:"Current Status", type:"text" },
  { name:"currentValue", label:"Current Investment Value (if known)", type:"number" },
  { name:"exitStatus", label:"Exit Status", type:"text" },
  { name:"role", label:"Investor Role", type:"text" },
  { name:"description", label:"Short Description", type:"textarea", span2:true },
];

export const SUCCESS_STORY_FIELDS = [
  { name:"company", label:"Company Name", type:"text" },
  { name:"year", label:"Year", type:"text" },
  { name:"location", label:"Location", type:"text" },
  { name:"story", label:"Story", type:"textarea", span2:true, maxWords:200 },
];
export const PORTFOLIO_FIELDS = [
  { name:"hasPastInvestments", label:"Have you invested in businesses previously?", type:"select", options:["Yes","No"] },
  { name:"activeInvestments", label:"Number of Active Investments", type:"number" },
  { name:"exitedInvestments", label:"Number of Exited Investments", type:"number" },
  { name:"portfolioCompanies", label:"Current Portfolio Companies", type:"text" },
  { name:"preferredSectors", label:"Preferred Sectors", type:"text" },
  { name:"typicalSize", label:"Typical Investment Size", type:"text" },
  { name:"typicalPerYear", label:"Typical Number of Investments Per Year", type:"number" },
  { name:"strategicSupport", label:"Strategic Support", type:"select-other", options:STRATEGIC_SUPPORT, otherKey:"strategicSupportOther" },
];

export const SWEAT_CONTRIBUTION_FIELDS = [
  { name:"contributionAreas", label:"Contribution Areas", type:"select-other", options:CONTRIBUTION_AREAS, required:true, otherKey:"contributionAreasOther" },
];
export const SWEAT_TIME_FIELDS = [
  { name:"timeCommitment", label:"How much time can you contribute?", type:"select-other", options:TIME_COMMITMENTS, required:true, otherKey:"timeCommitmentOther" },
];
export const SWEAT_ENGAGEMENT_FIELDS = [
  { name:"engagementType", label:"How would you like to contribute?", type:"select-other", options:ENGAGEMENT_TYPES, required:true, otherKey:"engagementTypeOther" },
];
export const SWEAT_PAST_FIELDS = [
  { name:"company", label:"Company Name", type:"text" },
  { name:"role", label:"Role", type:"text" },
  { name:"contribution", label:"Contribution", type:"text" },
  { name:"duration", label:"Duration", type:"text" },
  { name:"outcome", label:"Outcome", type:"textarea", span2:true },
  { name:"equityReceived", label:"Equity Received (if applicable)", type:"text" },
];

export const MENTOR_AREA_FIELDS = [
  { name:"mentoringAreas", label:"Mentoring Areas", type:"select-other", options:MENTOR_AREAS, required:true, otherKey:"mentoringAreasOther" },
];

export const BUSINESS_BASIC_FIELDS = [
  { name:"legalName", label:"Legal Company / Business Name", type:"text", required:true },
  { name:"email", label:"Email Address", type:"email", required:true },
  { name:"tradeName", label:"Trade / Brand Name", type:"text" },
  { name:"constitution", label:"Company Constitution", type:"text", required:true },
  { name:"businessType", label:"Business Type", type:"select-other", options:BUSINESS_TYPES, required:true, otherKey:"businessTypeOther" },
  { name:"businessCategory", label:"Business Category", type:"select-other", options:CATEGORIES, required:true, otherKey:"businessCategoryOther" },
  { name:"industry", label:"Industry", type:"select-other", options:INDUSTRIES, required:true, otherKey:"industryOther" },
  { name:"yearEstablished", label:"Year Established", type:"number", required:true },
  { name:"registeredAddress", label:"Registered Office Address", type:"text", required:true },
  { name:"operatingLocation", label:"Current Operating Location", type:"text" },
  { name:"offices", label:"Number of Current Offices", type:"number", required:true },
  { name:"employees", label:"Number of Employees", type:"number", required:true },
  { name:"website", label:"Website", type:"text" },
  { name:"contactPerson", label:"Contact Person", type:"text" },
  { name:"contactDesignation", label:"Contact Designation", type:"text" },
  { name:"mobile", label:"Mobile Number", type:"tel-country", codeKey:"mobileCode", required:true },
];
export const PARTNERSHIP_DETAIL_FIELDS = [
  { name:"partnershipFirmName", label:"Partnership Firm Name", type:"text" },
  { name:"numPartners", label:"Number of Partners", type:"number" },
  { name:"partnerNames", label:"Partner Names", type:"text", span2:true },
  { name:"partnerOwnership", label:"Ownership Percentage of Each Partner", type:"text", span2:true },
];
export const LEGAL_FIELDS = [
  { name:"regNumber", label:"Company Registration / Incorporation Number", type:"text" },
  { name:"pan", label:"PAN", type:"text" },
  { name:"gst", label:"GST Number", type:"text" },
  { name:"cin", label:"CIN / LLPIN (where applicable)", type:"text" },
  { name:"incorpDate", label:"Date of Incorporation", type:"text" },
  { name:"otherRegistrations", label:"Other Regulatory Registrations", type:"text" },
];
export const FINANCIAL_POSITION_FIELDS = [
  { name:"turnover", label:"Annual Turnover", type:"number", required:true },
  { name:"grossProfit", label:"Gross Profit", type:"number", required:true },
  { name:"netProfit", label:"Net Profit", type:"number", required:true },
  { name:"ebitda", label:"EBITDA (where applicable)", type:"number" },
  { name:"cashPosition", label:"Current Cash Position", type:"number" },
  { name:"monthlyOpex", label:"Monthly Operating Expenses", type:"number" },
  { name:"monthlyBurn", label:"Monthly Burn (if applicable)", type:"number" },
  { name:"breakEvenStatus", label:"Break-even Status", type:"text" },
  { name:"breakEvenDate", label:"Break-even Expected Date", type:"text" },
];
export const LOAN_FIELDS = [
  { name:"provider", label:"Loan Provider / Bank", type:"text" },
  { name:"originalAmount", label:"Original Loan Amount", type:"number" },
  { name:"amountPaid", label:"Amount Already Paid", type:"number" },
  { name:"outstanding", label:"Outstanding Amount", type:"number" },
  { name:"interestRate", label:"Interest Rate %", type:"number" },
  { name:"startDate", label:"Loan Start Date", type:"text" },
  { name:"endDate", label:"Last Repayment / Loan End Date", type:"text" },
  { name:"emi", label:"Monthly EMI", type:"number" },
  { name:"collateral", label:"Collateral / Security", type:"text", span2:true },
];
export const EXISTING_INVESTOR_FIELDS = [
  { name:"investorName", label:"Investor Name", type:"text" },
  { name:"investorType", label:"Investor Type", type:"text" },
  { name:"dateReceived", label:"Date Investment Received", type:"text" },
  { name:"amount", label:"Investment Amount", type:"number" },
  { name:"equityGiven", label:"Equity / Ownership % Given", type:"number" },
  { name:"investmentType", label:"Investment Type", type:"select", options:INVESTMENT_TYPES },
  { name:"currentOwnership", label:"Current Investor Ownership %", type:"number" },
  { name:"terms", label:"Investment Terms", type:"text", span2:true },
];
export const NEGOTIATION_FIELDS = [
  { name:"negInvestorName", label:"Investor Name", type:"text" },
  { name:"negAmount", label:"Expected Investment Amount", type:"number" },
  { name:"negEquity", label:"Expected Equity %", type:"number" },
  { name:"negStage", label:"Current Negotiation Stage", type:"text" },
];
export const CURRENT_OPS_FIELDS = [
  { name:"countriesServed", label:"Countries / Cities Served", type:"text" },
  { name:"activeCustomers", label:"Number of Active Customers", type:"number" },
  { name:"b2bClients", label:"Number of B2B Clients", type:"number" },
  { name:"b2cCustomers", label:"Number of B2C Customers", type:"number" },
  { name:"mainProducts", label:"Main Products / Services", type:"text" },
  { name:"competitors", label:"Major Competitors", type:"text" },
  { name:"coreRevenueSource", label:"Core Revenue Source", type:"text" },
  { name:"topProduct", label:"Top Revenue-Generating Product / Service", type:"text" },
  { name:"geographicMarket", label:"Geographic Market", type:"text" },
  { name:"marketSize", label:"Market Size (if known)", type:"text" },
  { name:"competitiveAdvantage", label:"Competitive Advantage", type:"textarea", span2:true, maxWords:200 },
];
export const FUNDING_FIELDS = [
  { name:"fundingAmount", label:"Funding Amount Required", type:"number", required:true },
  { name:"minAccepted", label:"Minimum Investment Accepted", type:"number" },
  { name:"maxAccepted", label:"Maximum Investment Accepted", type:"number" },
  { name:"equityOffered", label:"Equity Offered %", type:"number" },
  { name:"proposedValuation", label:"Proposed Valuation", type:"number" },
  { name:"preMoney", label:"Pre-Money Valuation (where applicable)", type:"number" },
  { name:"postMoney", label:"Post-Money Valuation (where applicable)", type:"number" },
  { name:"fundingType", label:"Funding Type", type:"select-other", options:FUNDING_TYPES, required:true, otherKey:"fundingTypeOther" },
];
export const PROPOSITION_FIELDS = [
  { name:"whyInvest", label:"Why should an investor invest in this company?", type:"textarea", span2:true, maxWords:200 },
  { name:"problemSolved", label:"What problem does the business solve?", type:"textarea", span2:true, maxWords:200 },
  { name:"differentiation", label:"What makes the company different?", type:"textarea", span2:true, maxWords:200 },
  { name:"whyNow", label:"Why now?", type:"textarea", span2:true, maxWords:200 },
  { name:"growthOpportunity", label:"What is the growth opportunity?", type:"textarea", span2:true, maxWords:200 },
  { name:"investorValue", label:"Expected investor value proposition", type:"textarea", span2:true, maxWords:200 },
];
export const MANAGEMENT_FIELDS = [
  { name:"founderName", label:"Founder Name", type:"text" },
  { name:"founderExperience", label:"Founder Experience", type:"text" },
  { name:"cofounderName", label:"Co-Founder Name", type:"text" },
  { name:"managementTeam", label:"Management Team", type:"text" },
  { name:"previousBusinesses", label:"Relevant Previous Businesses", type:"text" },
  { name:"industryExperience", label:"Years of Industry Experience", type:"number" },
  { name:"keyPositions", label:"Key Management Positions", type:"text" },
  { name:"professionalBackground", label:"Professional Background", type:"textarea", span2:true, maxWords:200 },
];

/* ---------------------------------------------------------------------------
   NAVIGATION / SCREEN CONFIGS
--------------------------------------------------------------------------- */
export const INVESTOR_TYPE_OPTIONS = [
  { key:"cash", title:"Cash Investor", desc:"Willing to Invest capital." },
  { key:"sweat", title:"Sweat Investor", desc:"Ready to Share Expertise" },
  { key:"mentor", title:"Mentor", desc:"Provides business advice " },
];

export const INVESTOR_PORTAL_NAV = [
  ["opportunities","Investment Opportunities"],["interests","My Interests"],
  ["portfolio","My Portfolio"],["profile","Investor Profile"],
  ["messages","Messages"],["notifications","Notifications"],["settings","Settings"],
];

export const SWEAT_PORTAL_NAV = [["opportunities","Opportunities"],["profile","My Profile"]];
export const MENTOR_PORTAL_NAV = [["network","Mentor Network"],["profile","My Profile"]];

export const BUSINESS_DOCUMENTS = ["Audited Financial Statements","Profit & Loss Statement","Balance Sheet","Cash Flow Statement","Bank Statements","Income Tax Returns","Loan Statements","Existing Investment Agreements","Shareholding / Cap Table","Pitch Deck","Business Plan","Other Supporting Documents"];

/* ---------------------------------------------------------------------------
   FINANCIAL TABLE HELPERS
--------------------------------------------------------------------------- */
export const HIST_ROWS_INIT = () => [0,1,2,3,4].map(() => ({}));
export const YEAR_AGO = (i) => `FY ${2026 - (4 - i)}`;
export const YEAR_AHEAD = (i) => `Year ${i + 1}`;

/* ---------------------------------------------------------------------------
   INLINE FIELD CONFIGS (used in BusinessStatus form)
--------------------------------------------------------------------------- */
export const PARTNERSHIP_STATUS_FIELDS = [
  { name:"isPartnership", label:"Is this a Partnership Firm?", type:"select", options:["Yes","No"], required:true },
  { name:"hasLoans", label:"Does the company currently have any loans?", type:"select", options:["Yes","No"], required:true },
  { name:"hasExistingInvestment", label:"Has the company received investment previously?", type:"select", options:["Yes","No"], required:true },
  { name:"inNegotiation", label:"Is any investment negotiation currently in progress?", type:"select", options:["Yes","No"] },
];

export const LOAN_STATUS_FIELDS = [
  { name:"otherLiabilities", label:"Other Financial Liabilities (vendor payments, credit facilities, other debts)", type:"textarea", span2:true, maxWords:200 },
];

/* ---------------------------------------------------------------------------
   REVIEW SECTION FIELD CONFIGS
--------------------------------------------------------------------------- */
export const PARTNERSHIP_REVIEW_FIELDS = [
  { name:"isPartnership", label:"Is this a Partnership Firm?", type:"select" },
  { name:"hasLoans", label:"Does the company currently have any loans?", type:"select" },
  { name:"hasExistingInvestment", label:"Has the company received investment previously?", type:"select" },
  { name:"inNegotiation", label:"Is any investment negotiation currently in progress?", type:"select" },
  { name:"otherLiabilities", label:"Other Financial Liabilities", type:"textarea", maxWords:200 },
];

export const PROJECTION_REVIEW_FIELDS = [
  { name:"vision5yr", label:"What is your 5-year vision?", type:"textarea" },
  { name:"growthDrivers", label:"What will drive the projected growth?", type:"textarea" },
  { name:"expansionPlans", label:"Expansion Plans", type:"select-other", otherKey:"expansionPlansOther" },
];

export const PERSONAL_REVIEW_FIELDS = () => PERSONAL_FIELDS();

export const INVESTMENT_DETAILS_REVIEW_FIELDS = [...CASH_INVESTMENT_CAPACITY_FIELDS, ...CASH_PREFERENCES_FIELDS];

export const PORTFOLIO_REVIEW_FIELDS = PORTFOLIO_FIELDS;

export const SWEAT_PERSONAL_REVIEW_FIELDS = () => [...PERSONAL_FIELDS(true), {name:"coreSkills", label:"Core Skills", type:"text"}];

export const SWEAT_CONTRIBUTION_REVIEW_FIELDS = [
  ...SWEAT_CONTRIBUTION_FIELDS,
  ...SWEAT_TIME_FIELDS,
  ...SWEAT_ENGAGEMENT_FIELDS,
  { name:"hasPrevExperience", label:"Have you previously helped businesses without direct cash investment?", type:"select", options:["Yes","No"] },
  { name:"equityPreference", label:"Are you willing to receive equity in exchange for your contribution?", type:"select", options:["Yes","No","Open to Discussion"] },
];

export const MENTOR_PERSONAL_REVIEW_FIELDS = () => PERSONAL_FIELDS(true);

export const MENTORING_REVIEW_FIELDS = [
  ...MENTOR_AREA_FIELDS,
  { name:"hasMentored", label:"Have you previously mentored founders/businesses?", type:"select", options:["Yes","No"] },
  { name:"mentoringYears", label:"Years of Mentoring Experience", type:"number" },
  { name:"businessesMentored", label:"Number of Businesses Mentored", type:"number" },
  { name:"typesMentored", label:"Types of Businesses Mentored", type:"text" },
];

export const MENTOR_STORY_FIELDS = [
  { name:"company", label:"Company Name", type:"text" },
  { name:"year", label:"Year", type:"text" },
  { name:"location", label:"Location", type:"text" },
  { name:"story", label:"Story", type:"textarea" },
];
